// Resuelve el roomId de Digital Samba de una cita de telemedicina, a partir
// del citaId de la bóveda (Rediseño mayor 2026-08-31, Módulo G) — dos
// búsquedas en cadena porque el Encounter no lleva el citaId directo, lleva
// `appointment: [{reference: "Appointment/<id>"}]` (ver
// telemedicina-bridge.ts): 1) Appointment por CITA_ID_SYSTEM, 2) Encounter
// por ese Appointment. Mismas dos llamadas que ya hace
// asegurarEncounterTelemedicina del lado servidor, pero de solo lectura acá
// (nunca crea nada) — si el sweep de 1h antes todavía no corrió, `roomId`
// queda `null` y la UI lo explica en vez de fallar.
import type { Encounter } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';

// Mismos literales que packages/web/src/api/lib/medplum-sync.ts (CITA_ID_SYSTEM)
// y telemedicina-bridge.ts (EXT_MODALIDAD_CONSULTA) — paquetes distintos, no
// se pueden compartir, mantener en sync a mano si cambian.
const CITA_ID_SYSTEM = 'http://logiacare.ec/fhir/identifier/cita-id';
const EXT_MODALIDAD_CONSULTA = 'http://logiacare.ec/fhir/modalidad-consulta';

export type EstadoSalaDeCita = { cargando: true } | { cargando: false; roomId: string | null };

export function useSalaDeCita(citaId: string | undefined): EstadoSalaDeCita {
  const medplum = useMedplum();
  const [roomId, setRoomId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!citaId) {
      setRoomId(null);
      return;
    }
    let cancelado = false;
    setRoomId(undefined);
    (async () => {
      const appointment = await medplum.searchOne('Appointment', { identifier: `${CITA_ID_SYSTEM}|${citaId}` });
      if (cancelado) return;
      if (!appointment?.id) {
        setRoomId(null);
        return;
      }
      const encounter = await medplum.searchOne('Encounter', { appointment: `Appointment/${appointment.id}` });
      if (cancelado) return;
      const roomIdEncontrado = extraerRoomId(encounter);
      setRoomId(roomIdEncontrado);
    })().catch(() => {
      if (!cancelado) setRoomId(null);
    });
    return () => {
      cancelado = true;
    };
  }, [medplum, citaId]);

  if (roomId === undefined) return { cargando: true };
  return { cargando: false, roomId };
}

function extraerRoomId(encounter: Encounter | undefined): string | null {
  const ext = encounter?.extension?.find((e) => e.url === EXT_MODALIDAD_CONSULTA);
  return ext?.extension?.find((e) => e.url === 'sala-id')?.valueString ?? null;
}
